import React, { useEffect } from 'react';
import StbLoader from '../../../security-toolbox-ui/StbLoader';

type Props = {
  isPassed: boolean | null;        // null = loading, true/false = known
  scorePercent: number | null;
  onContinue: () => void;
};

// Fallback loader for QuizResultPage
const FallbackLoader: React.FC = () => (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: '2rem',
    margin: '2rem 0',
    borderRadius: '12px',
    border: '3px solid #90caf9',
    backgroundColor: '#e3f2fd',
    minHeight: '200px'
  }}>
    <div style={{
      width: '48px',
      height: '48px',
      border: '4px solid rgba(21, 101, 192, 0.2)',
      borderTop: '4px solid #1565c0',
      borderRadius: '50%',
      animation: 'spin 2s linear infinite',
      marginBottom: '1rem'
    }} />
    <h2 style={{ color: '#1565c0', margin: '0 0 0.5rem 0' }}>
      Checking your result...
    </h2>
    <p style={{ color: '#1976d2', margin: 0 }}>
      Please wait while we calculate your score
    </p>
    <style>{`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

// Safe loader wrapper for result page
const SafeResultLoader: React.FC = () => {
  try {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: '2rem',
        margin: '2rem 0',
        borderRadius: '12px',
        border: '3px solid #90caf9',
        backgroundColor: '#e3f2fd',
        minHeight: '200px'
      }}>
        <StbLoader isLoading={true} />
        <h2 style={{ color: '#1565c0', margin: '1rem 0 0.5rem 0' }}>
          Checking your result...
        </h2>
      </div>
    );
  } catch (error) {
    console.warn('StbLoader failed in QuizResultPage, using fallback:', error);
    return <FallbackLoader />;
  }
};

const QuizResultPage: React.FC<Props> = ({ isPassed, scorePercent, onContinue }) => {
  // Fire event when passed
  useEffect(() => {
    if (isPassed === true) {
      window.dispatchEvent(new CustomEvent('moodle:activityCompleted'));
    }
  }, [isPassed]);

  if (isPassed === null) {
    // Loading state
    return <SafeResultLoader />;
  }

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      padding: '2rem',
      margin: '2rem 0',
      borderRadius: '12px',
      border: `3px solid ${isPassed ? '#4caf50' : '#d32f2f'}`,
      backgroundColor: isPassed ? '#e8f5e9' : '#ffebee',
    }}>
      <span style={{ fontSize: '4rem' }} role="img" aria-label={isPassed ? 'Success' : 'Failure'}>
        {isPassed ? '🎉' : '🤔'}
      </span>
      <h2 style={{ color: isPassed ? '#2e7d32' : '#b71c1c', margin: '1rem 0 0.5rem 0' }}>
        {isPassed ? 'Congratulations, you passed!' : 'Attempt Finished'}
      </h2>
      {scorePercent !== null && (
        <p style={{ fontSize: '1.25rem', fontWeight: 600, margin: 0 }}>
          Your Score: {scorePercent.toFixed(0)}%
        </p>
      )}
      <p style={{ color: '#555', maxWidth: '400px', margin: '1rem 0' }}>
        {isPassed
          ? 'Great job! You can now review your answers or proceed to the next activity.'
          : 'You can review your answers to see where you can improve.'}
      </p>
      <button
        onClick={onContinue}
        style={{
          marginTop: '1.5rem',
          padding: '0.75rem 2rem',
          fontSize: '1rem',
          fontWeight: 600,
          color: 'white',
          background: '#0a66ff',
          border: 'none',
          borderRadius: '8px',
          cursor: 'pointer',
          transition: 'all 0.2s ease'
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.background = '#095ae0';
          e.currentTarget.style.transform = 'translateY(-1px)';
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.background = '#0a66ff';
          e.currentTarget.style.transform = 'translateY(0)';
        }}
      >
        Continue to Review
      </button>
    </div>
  );
};

export default QuizResultPage;